import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import AIFloatingChat from './AIFloatingChat';
import CommandBar from './CommandBar';
import {
  LayoutDashboard,
  Users,
  FolderKanban,
  CheckSquare,
  TrendingUp,
  LogOut,
  Menu,
  X,
  Shield,
  BookOpen,
  Truck,
  DollarSign,
  FileText,
  Wrench,
  Clock,
  Package,
  Calendar,
  AlertTriangle,
  Award,
  FileBarChart,
  ClipboardCheck,
  ClipboardList,
  Banknote,
  Store,
  FolderOpen,
  Receipt,
  Mail,
  Globe,
  Calculator,
  MessageSquare,
  Video,
  Building2,
  HardHat,
  Bot,
  Database,
} from 'lucide-react';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/auth');
  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/clients', label: 'Clients', icon: Users },
    { path: '/projects', label: 'Projects', icon: FolderKanban },
    { path: '/tasks', label: 'Tasks', icon: CheckSquare },
    { path: '/work-orders', label: 'Work Orders', icon: ClipboardList },
    { path: '/employees', label: 'Employees', icon: TrendingUp },
    { path: '/handbook', label: 'Handbook/Policies', icon: BookOpen },
  ];

  // Communication tools - Email only (Chat & Meetings are in newFeatures)
  const communicationItems = [
    { path: '/email', label: 'Email', icon: Mail },
  ];

  // Add new features for ALL users - Tools & Communication
  const newFeatures = [
    { path: '/chat', label: 'Team Chat', icon: MessageSquare },
    { path: '/meetings', label: 'Video Meetings', icon: Video },
    { path: '/email', label: 'Email', icon: Mail },
    { path: '/browser', label: 'Browser', icon: Globe },
  ];

  // AI Operations - Command Center removed from sidebar (accessed via Dashboard)

  // Estimating - Admin/Manager only
  const estimatingItems = [
    { path: '/estimating', label: 'Estimating', icon: Calculator, adminOnly: true },
  ];

  // Fleet Inspections - Hidden from vendors
  const isVendor = user.role === 'vendor';
  if (!isVendor) {
    navItems.push({ path: '/fleet', label: 'Fleet Inspections', icon: Truck });
  }

  // Financial Management - Admin/Manager only
  const isAdminOrManager = user.role === 'admin' || user.role === 'manager';
  const financialItems = [
    { path: '/invoices', label: 'Invoices', icon: DollarSign, adminOnly: true },
    { path: '/expenses', label: 'Expenses', icon: FileText, adminOnly: true },
  ];

  // Contracts - Admin/Manager AND Vendors (vendors see only their contracts)
  const contractItems = [
    { path: '/contracts', label: 'Contracts', icon: FileText },
  ];

  // Equipment - Visible to all
  const equipmentItems = [
    { path: '/equipment', label: 'Equipment/Assets', icon: Wrench },
  ];

  // Operations - Timesheets hidden from vendors
  const operationsItems = [
    { path: '/forms', label: 'Forms', icon: FileText },
    { path: '/inventory', label: 'Inventory', icon: Package },
    { path: '/schedules', label: 'Schedules', icon: Calendar },
  ];
  
  // Add Timesheets only for non-vendors
  if (!isVendor) {
    operationsItems.unshift({ path: '/timesheets', label: 'Timesheets', icon: Clock });
  }

  // Safety & Compliance - Admin/Manager AND Vendors
  const safetyComplianceItems = [
    { path: '/safety-reports', label: 'Safety Reports', icon: AlertTriangle },
    { path: '/certifications', label: 'Certifications', icon: Award },
    { path: '/reports', label: 'Reports', icon: FileBarChart, adminOnly: true },
    { path: '/compliance', label: 'Compliance', icon: ClipboardCheck },
  ];

  // Add Admin link if user is admin
  const isAdmin = user.role === 'admin';
  if (isAdmin) {
    navItems.push({ path: '/admin', label: 'Admin Panel', icon: Shield });
  }

  return (
    <div className="flex h-screen bg-black plaid-bg relative" data-testid="main-layout">
      {/* Brass Lighting Effect */}
      <div className="fixed inset-0 brass-lighting pointer-events-none z-0"></div>
      
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Hover trigger area for sidebar */}
      <div 
        className="fixed inset-y-0 left-0 w-4 z-40"
        onMouseEnter={() => setSidebarOpen(true)}
      />

      {/* Sidebar - Black Background with Gold Text */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-black border-r-2 border-[#C9A961] transform transition-all duration-300 ease-in-out shadow-2xl ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
        onMouseEnter={() => setSidebarOpen(true)}
        onMouseLeave={() => setSidebarOpen(false)}
      >
        <div className="flex flex-col h-full">
          
          {/* Logo Section - BLACK Background to blend with logo */}
          <div className="p-8 border-b-2 border-black relative bg-black">
            <div className="relative">
              <img 
                src="/williams-logo-transparent.png" 
                alt="Williams Diversified LLC" 
                className="w-44 h-auto mb-4 mx-auto hover:scale-110 transition-transform duration-500"
              />
              <div className="text-center space-y-1">
                <h1 className="text-2xl font-bold">
                  <span className="text-white">Williams</span>{' '}
                  <span className="text-[#C9A961]">Diversified</span>
                </h1>
                <p className="text-sm text-[#C9A961]/70 tracking-wide">Project Command Center</p>
              </div>
            </div>
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden absolute top-4 right-4 hover:bg-[#C9A961]/20 hover:border-l-4 hover:border-[#C9A961]"
            onClick={() => setSidebarOpen(false)}
            style={{ color: '#C9A961' }}
          >
            <X className="h-5 w-5" />
          </Button>

          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {/* Main Navigation */}
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant="ghost"
                    className="w-full justify-start hover:bg-[#C9A961]/20 hover:border-l-4 hover:border-[#C9A961] transition-all duration-200 group"
                    style={isActive ? { backgroundColor: '#C9A961', color: '#000000' } : { color: '#C9A961' }}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <Icon className="mr-3 h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    {item.label}
                  </Button>
                </Link>
              );
            })}

            {/* New Features Section - Email & Browser */}
            <div className="pt-4 pb-2">
              <p className="text-xs font-semibold uppercase tracking-wider px-3 text-[#C9A961]/60">
                Tools & Communication
              </p>
            </div>
            {newFeatures.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant="ghost"
                    className="w-full justify-start hover:bg-[#C9A961]/20 hover:border-l-4 hover:border-[#C9A961] transition-all duration-200 group"
                    style={isActive ? { backgroundColor: '#C9A961', color: '#000000' } : { color: '#C9A961' }}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="mr-3 h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    {item.label}
                  </Button>
                </Link>
              );
            })}

            {/* Estimating Section - Admin/Manager only */}
            {isAdminOrManager && (
              <>
                <div className="pt-4 pb-2">
                  <p className="text-xs font-semibold uppercase tracking-wider px-3" style={{ color: '#C9A961', opacity: 0.6 }}>
                    Estimating
                  </p>
                </div>
                {estimatingItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link key={item.path} to={item.path}>
                      <Button
                        variant="ghost"
                        className="w-full justify-start hover:bg-[#C9A961]/20 hover:border-l-4 hover:border-[#C9A961] transition-all duration-200 group"
                        style={isActive ? { backgroundColor: '#C9A961', color: '#000000' } : { color: '#C9A961' }}
                        data-testid={`nav-${item.label.toLowerCase()}`}
                      >
                        <Icon className="mr-3 h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                        {item.label}
                      </Button>
                    </Link>
                  );
                })}
              </>
            )}

            {/* Financial Management Section - Admin/Manager only */}
            {isAdminOrManager && (
              <>
                <div className="pt-4 pb-2">
                  <p className="text-xs font-semibold uppercase tracking-wider px-3" style={{ color: '#C9A961', opacity: 0.6 }}>
                    Financial Management
                  </p>
                </div>
                {financialItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link key={item.path} to={item.path}>
                      <Button
                        variant="ghost"
                        className="w-full justify-start hover:bg-[#C9A961]/20 hover:border-l-4 hover:border-[#C9A961]"
                        style={isActive ? { backgroundColor: '#C9A961', color: '#000000' } : { color: '#C9A961' }}
                        data-testid={`nav-${item.label.toLowerCase()}`}
                      >
                        <Icon className="mr-3 h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                        {item.label}
                      </Button>
                    </Link>
                  );
                })}
              </>
            )}

            {/* Contracts Section - Visible to all (vendors see only their contracts) */}
            <div className="pt-4 pb-2">
              <p className="text-xs font-semibold uppercase tracking-wider px-3" style={{ color: '#C9A961', opacity: 0.6 }}>
                Contracts
              </p>
            </div>
            {contractItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant="ghost"
                    className="w-full justify-start hover:bg-[#C9A961]/20 hover:border-l-4 hover:border-[#C9A961]"
                    style={isActive ? { backgroundColor: '#C9A961', color: '#000000' } : { color: '#C9A961' }}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="mr-3 h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    {item.label}
                  </Button>
                </Link>
              );
            })}

            {/* Equipment Section - Visible to all */}
            <div className="pt-4 pb-2">
              <p className="text-xs font-semibold uppercase tracking-wider px-3" style={{ color: '#C9A961', opacity: 0.6 }}>
                Equipment
              </p>
            </div>
            {equipmentItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant="ghost"
                    className="w-full justify-start hover:bg-[#C9A961]/20 hover:border-l-4 hover:border-[#C9A961]"
                    style={isActive ? { backgroundColor: '#C9A961', color: '#000000' } : { color: '#C9A961' }}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="mr-3 h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    {item.label}
                  </Button>
                </Link>
              );
            })}

            {/* Operations Section - Visible to all */}
            <div className="pt-4 pb-2">
              <p className="text-xs font-semibold uppercase tracking-wider px-3" style={{ color: '#C9A961', opacity: 0.6 }}>
                Operations
              </p>
            </div>
            {operationsItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant="ghost"
                    className="w-full justify-start hover:bg-[#C9A961]/20 hover:border-l-4 hover:border-[#C9A961]"
                    style={isActive ? { backgroundColor: '#C9A961', color: '#000000' } : { color: '#C9A961' }}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="mr-3 h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    {item.label}
                  </Button>
                </Link>
              );
            })}

            {/* Safety & Compliance Section - Visible to all (Reports admin only) */}
            <div className="pt-4 pb-2">
              <p className="text-xs font-semibold uppercase tracking-wider px-3" style={{ color: '#C9A961', opacity: 0.6 }}>
                Safety & Compliance
              </p>
            </div>
            {safetyComplianceItems.map((item) => {
              // Hide Reports from non-admin users
              if (item.adminOnly && !isAdminOrManager) return null;
              
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant="ghost"
                    className="w-full justify-start hover:bg-[#C9A961]/20 hover:border-l-4 hover:border-[#C9A961]"
                    style={isActive ? { backgroundColor: '#C9A961', color: '#000000' } : { color: '#C9A961' }}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="mr-3 h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    {item.label}
                  </Button>
                </Link>
              );
            })}

            {/* Company Documents Section - Vendors only */}
            {isVendor && (
              <>
                <div className="pt-4 pb-2">
                  <p className="text-xs font-semibold uppercase tracking-wider px-3" style={{ color: '#C9A961', opacity: 0.6 }}>
                    Company Documents
                  </p>
                </div>
                <Link to="/company-documents">
                  <Button
                    variant="ghost"
                    className="w-full justify-start hover:bg-[#C9A961]/20 hover:border-l-4 hover:border-[#C9A961]"
                    style={location.pathname === '/company-documents' ? { backgroundColor: '#000000', color: '#C9A961' } : { color: '#000000' }}
                    data-testid="nav-company-documents"
                  >
                    <FolderOpen className="mr-3 h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    My Documents
                  </Button>
                </Link>
              </>
            )}

            {/* My Payroll Documents Section - Employees only (not vendors) */}
            {!isVendor && (
              <>
                <div className="pt-4 pb-2">
                  <p className="text-xs font-semibold uppercase tracking-wider px-3" style={{ color: '#C9A961', opacity: 0.6 }}>
                    My Payroll
                  </p>
                </div>
                <Link to="/my-payroll-documents">
                  <Button
                    variant="ghost"
                    className="w-full justify-start hover:bg-[#C9A961]/20 hover:border-l-4 hover:border-[#C9A961]"
                    style={location.pathname === '/my-payroll-documents' ? { backgroundColor: '#000000', color: '#C9A961' } : { color: '#000000' }}
                    data-testid="nav-my-payroll-documents"
                  >
                    <Receipt className="mr-3 h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    My Payroll Documents
                  </Button>
                </Link>
              </>
            )}

            {/* Payroll Section - Admin/Manager only */}
            {isAdminOrManager && (
              <>
                <div className="pt-4 pb-2">
                  <p className="text-xs font-semibold uppercase tracking-wider px-3" style={{ color: '#C9A961', opacity: 0.6 }}>
                    Payroll
                  </p>
                </div>
                <Link to="/payroll">
                  <Button
                    variant="ghost"
                    className="w-full justify-start hover:bg-[#C9A961]/20 hover:border-l-4 hover:border-[#C9A961]"
                    style={location.pathname === '/payroll' ? { backgroundColor: '#000000', color: '#C9A961' } : { color: '#000000' }}
                    data-testid="nav-payroll"
                  >
                    <Banknote className="mr-3 h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    Payroll Management
                  </Button>
                </Link>
              </>
            )}

            {/* Vendor Portal Section - Visible to all */}
            <div className="pt-4 pb-2">
              <p className="text-xs font-semibold uppercase tracking-wider px-3" style={{ color: '#C9A961', opacity: 0.6 }}>
                Vendor Portal
              </p>
            </div>
            <Link to="/vendors">
              <Button
                variant="ghost"
                className="w-full justify-start hover:bg-[#C9A961]/20 hover:border-l-4 hover:border-[#C9A961]"
                style={location.pathname === '/vendors' ? { backgroundColor: '#C9A961', color: '#000000' } : { color: '#C9A961' }}
                data-testid="nav-vendors"
              >
                <Store className="mr-3 h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                {user.role === 'vendor' ? 'My Vendor Portal' : 'Vendor Management'}
              </Button>
            </Link>
          </nav>

          <div className="p-4 border-t border-[#C9A961]">
            <div className="flex items-center mb-4">
              <div className="flex-1">
                <p className="text-sm font-medium text-[#C9A961]">{user.username}</p>
                <p className="text-xs text-[#C9A961]/70">{user.email}</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full justify-start hover:bg-[#C9A961]/20 hover:border-l-4 hover:border-[#C9A961] border-[#C9A961] text-[#C9A961]"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="mr-3 h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto bg-transparent w-full relative z-10">
        <div className="lg:hidden p-4 bg-black border-b border-[#C9A961]">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            data-testid="menu-button"
            className="text-[#C9A961]"
          >
            <Menu className="h-6 w-6" />
          </Button>
        </div>
        <div className="p-6">
          <Outlet />
        </div>
        
        {/* AI Floating Chat Widget */}
        <AIFloatingChat currentPage={location.pathname} />
        <CommandBar />
      </main>
    </div>
  );
};

export default Layout;
